import Button from '@/common/components/Button'
import CardNumberWithCopyButton from '@/common/components/CardNumberWithCopyButton'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import { FailedConsumptionData } from '@/common/types/client/failedConsumption.type'
import { formatCurrency, formatDateTime, formatUnit } from '@/common/utils/formatters'
import { transformToNumber } from '@/common/utils/transformers'
import { CompanyFailedConsumptionDetails } from '@/features/privateCompanies/components/companyFailedConsumptionDetails'
import { ClientFailedConsumptionTableItem } from '@/features/privateCompanies/types/clientFailedConsumption.type'
import { SearchIcon, UserRoundIcon } from 'lucide-react'
import React from 'react'
import { Tooltip } from 'react-tooltip'

export class ClientFailedConsumptionsTransformer {
  static tableTransformer({
    redirectToConsumption,
    disableActions,
  }: {
    redirectToConsumption?: ({
      cardId,
      establishment,
      product,
    }: {
      cardId: string
      establishment: { id: string; label: string }
      product: { id: string; displayName: string }
    }) => void
    disableActions?: boolean
  }): (events: FailedConsumptionData[]) => ClientFailedConsumptionTableItem[] {
    return (events) => {
      return events.map((event) => {
        const totalAmount = transformToNumber({ value: event.total_amount })

        return {
          protocol_number: event.protocol_number,
          eventable_type: eventableTypeTranslations['Client::Consumption'],
          details: React.createElement(CompanyFailedConsumptionDetails, { data: event }),
          total_amount: Number.isNaN(totalAmount) ? '--' : formatCurrency(totalAmount),
          branch: event?.client_branch?.display_name || '--',
          card: event.card ? <CardNumberWithCopyButton card={event.card} /> : '',
          usage_declaration: event.current_equipment_usage
            ? formatUnit(event.current_equipment_usage, event.current_equipment_usage_unit)
            : '--',
          capture_method: event.capture_method?.display_name || '',
          created_at: formatDateTime(new Date(event.created_at)),
          license_plate: event?.card?.equipment_field_for_display_value || '',
          buyer: (
            <div className="flex items-center gap-[10px]">
              <ImageWithPlaceholder
                url={event.buyer?.profile_picture?.url}
                alt={event.buyer?.full_name}
                className="size-6"
                iconClassName="size-4"
                placeholderIcon={UserRoundIcon}
              />
              <p>{event.buyer?.full_name}</p>
            </div>
          ),
          actions: !!redirectToConsumption && (
            <>
              <Button
                data-tooltip-id={`${event.protocol_number}`}
                className="bg-transparent hover:bg-transparent disabled:hover:bg-transparent p-0 mx-auto"
                onClick={() => {
                  redirectToConsumption({
                    cardId: event?.card?.id || '',
                    establishment: {
                      id: event?.establishment_branch?.id || '',
                      label: `${event?.establishment_branch?.general_information?.fantasy_name}${
                        !!event?.establishment_branch?.general_information?.official_name
                          ? ` (${event?.establishment_branch?.general_information?.official_name})`
                          : ''
                      }`,
                    },
                    product: {
                      id: event?.line_items?.[0]?.product?.id || '',
                      displayName: event?.line_items?.[0]?.product?.display_name || '',
                    },
                  })
                }}
                disabled={disableActions}
                operationKlassToCheckPermission="Admin::Client::Cards::CurrentLimits"
              >
                <SearchIcon strokeWidth={1} />
              </Button>

              <Tooltip id={`${event.protocol_number}`}>
                Acesso direto à tela de consulta para o equipamento e estabelecimento dessa transação.
              </Tooltip>
            </>
          ),
        }
      })
    }
  }
}

const eventableTypeTranslations: { [key: string]: string } = {
  'Client::Consumption': 'Consumo',
  'Billing::Refund': 'Estorno',
}
